import { api } from '../../api'
import type {
  SAW,
  SAWFilters,
  SAWCountResponse,
  SAWImportPayload,
  SAWEditPayload,
  SAWBulkUpdatePayload,
  SawBrandRemark,
  SawBrandValue,
  Intermediary,
  IntermediaryGroup,
  IntermediaryFilters,
  CreateIntermediaryPayload,
  UpdateIntermediaryPayload,
  SawTag,
  SawTagFilters,
  SawComment,
  CreateSawCommentPayload,
  SawNotice,
  SawNoticeBrand,
  SawNoticeStep,
  SawNoticeStepPayload,
  BulkSawNoticePayload,
  EditSawNoticeFlowPayload,
  CreateSawNoticePayload,
  SawNoticePreviewPayload,
  GetNoticePayload,
  UpdateNoticeItemsPayload,
  UpdateNoticeAttachmentsPayload,
  SawLineChartResponse,
  DPSearchMode,
  DPKeywordTarget,
  DPHostnameParams,
  DPCompanyParams,
  DPKeywordParams,
  DPLocalityParams,
  DPIpParams,
  DPPhoneParams,
  DPDomainNameParams,
  DPCybersquattingParams,
  DPOutgoingParams,
  DPModeParams,
  DPSearchOptions,
  DPSearchRequest,
  DPSearchRecord,
  DPSearchResponse,
  DPConsoleFieldGroup,
  DPConsoleFilter,
  DPConsoleRelationshipFilter,
  DPConsoleImportEvent,
  DPConsoleSearchRequest,
  DPConsoleSearchResponse,
  DPConsoleHit,
  DPConsoleTermsRequest,
  DPConsoleTermsBucket,
} from '../../../types/saw.types'

export type {
  SAW,
  SAWFilters,
  SAWCountResponse,
  SAWImportPayload,
  SAWEditPayload,
  SAWBulkUpdatePayload,
  SawBrandRemark,
  SawBrandValue,
  Intermediary,
  IntermediaryGroup,
  IntermediaryFilters,
  CreateIntermediaryPayload,
  UpdateIntermediaryPayload,
  SawTag,
  SawTagFilters,
  SawComment,
  CreateSawCommentPayload,
  SawNotice,
  SawNoticeStep,
  SawNoticeStepPayload,
  BulkSawNoticePayload,
  EditSawNoticeFlowPayload,
  CreateSawNoticePayload,
  SawNoticePreviewPayload,
  GetNoticePayload,
  UpdateNoticeItemsPayload,
  UpdateNoticeAttachmentsPayload,
  SawLineChartResponse,
  DPSearchMode,
  DPKeywordTarget,
  DPHostnameParams,
  DPCompanyParams,
  DPKeywordParams,
  DPLocalityParams,
  DPIpParams,
  DPPhoneParams,
  DPDomainNameParams,
  DPCybersquattingParams,
  DPOutgoingParams,
  DPModeParams,
  DPSearchOptions,
  DPSearchRequest,
  DPSearchRecord,
  DPSearchResponse,
  DPConsoleFieldGroup,
  DPConsoleFilter,
  DPConsoleRelationshipFilter,
  DPConsoleImportEvent,
  DPConsoleSearchRequest,
  DPConsoleSearchResponse,
  DPConsoleHit,
  DPConsoleTermsRequest,
  DPConsoleTermsBucket,
} from '../../../types/saw.types'

// --- SAW List & Detail ---

export const getSAW = async (params: SAWFilters): Promise<{
  data: SAW[]
  count: number
  totalPages: number
  hasNext: boolean
  nextPage: number | null
}> => {
  const res = await api.get<{ data: SAW[]; count: number; totalPages: number; hasNext: boolean; nextPage: number | null }>('saw/', { params })
  return res.data
}

export const getSAWCount = async (params: Partial<SAWFilters>): Promise<SAWCountResponse> => {
  const res = await api.get<SAWCountResponse>('saw/count/', { params })
  return res.data
}

export const getSAWTlds = async (): Promise<{ tlds: string[] }> => {
  const res = await api.get<{ tlds: string[] }>('saw/tlds/')
  return res.data
}

export const getSAWDynamicsCases = async (search?: string): Promise<{ dynamics_cases: string[] }> => {
  const res = await api.get<{ dynamics_cases: string[] }>('saw/dynamics-cases/', {
    params: search ? { search } : undefined,
  })
  return res.data
}

export const getSAWDetail = async (params: {
  id: string
  fields?: string
  expand?: string
  otp_token?: string
}): Promise<SAW> => {
  const res = await api.get<SAW>(`saw/${params.id}/`, {
    params: { fields: params.fields, expand: params.expand, otp_token: params.otp_token },
  })
  return res.data
}

export const importSAW = async (data: SAWImportPayload): Promise<SAW[]> => {
  const res = await api.post<SAW[]>('saw/', data)
  return res.data
}

export const sawEdit = async (data: SAWEditPayload): Promise<SAW> => {
  const res = await api.patch<SAW>(`saw/${data.id}/`, data)
  return res.data
}

export const deleteSAW = async (data: { id: string }): Promise<void> => {
  await api.delete(`saw/${data.id}/`)
}

export const bulkUpdateSAW = async (data: SAWBulkUpdatePayload): Promise<void> => {
  await api.patch('saw/bulk_update/', data)
}

export const bulkCloseSAW = async (ids: string[]): Promise<void> => {
  await api.patch('saw/bulk_close/', { ids })
}

// Close whole domains from a Site-Items selection: the backend resolves the distinct
// saws behind these item ids and closes them.
export const bulkCloseSAWByItems = async (itemIds: string[]): Promise<void> => {
  await api.patch('saw/bulk_close_by_items/', { item_ids: itemIds })
}

// --- SAW Stats ---

export const getSawMostItems = async (params: {
  fields?: string
  expand?: string
  page_size: number
  page?: number
}): Promise<{ data: SAW[]; nextCursor: number | null; lastPage: boolean }> => {
  const res = await api.get<{ data: SAW[]; nextCursor: number | null; lastPage: boolean }>(
    'saw/',
    { params: { ordering: '-num_items', ...params } },
  )
  return res.data
}

export const getSawLineChart = async (params: { period: string }): Promise<SawLineChartResponse> => {
  const res = await api.get<SawLineChartResponse>('saw/stats', { params })
  return res.data
}

// --- SAW Tags ---

export const getSAWTags = async (params: SawTagFilters): Promise<{ data: SawTag[]; count: number }> => {
  const res = await api.get<{ data: SawTag[]; count: number }>('saw/tags/', { params })
  return res.data
}

export const deleteSAWTag = async (name: string): Promise<void> => {
  await api.delete(`saw/tags/${name}/`, { validateStatus: (status) => status === 204 || status === 404 })
}

// --- SAW Notices ---

export const createSawNotice = async (data: CreateSawNoticePayload): Promise<SawNotice> => {
  const res = await api.post<SawNotice>(`saw/${data.id}/notices/`, data)
  return res.data
}

export const editSawNoticeFlow = async (data: EditSawNoticeFlowPayload): Promise<SawNotice> => {
  const res = await api.patch<SawNotice>(`saw/${data.id}/notices/${data.notice_id}/`, data)
  return res.data
}

export const getNotice = async (data: GetNoticePayload): Promise<SawNotice> => {
  const res = await api.get<SawNotice>(`saw/${data.id}/notices/${data.notice_id}/`, {
    params: { fields: data.fields, expand: data.expand },
  })
  return res.data
}

export const getNoticeItemIds = async (data: { id: string; notice_id: string }): Promise<string[]> => {
  const res = await api.get<{ items: string[] }>(`saw/${data.id}/notices/${data.notice_id}/`, { params: { fields: 'items' } })
  return res.data.items
}

export const updateNoticeItems = async (data: UpdateNoticeItemsPayload): Promise<UpdateNoticeItemsPayload> => {
  if (data.item_id) {
    await api.post(`saw/${data.id}/notices/${data.notice_id}/items/`, data)
  }
  if (data.deleted_id) {
    await api.delete(`saw/${data.id}/notices/${data.notice_id}/items/${data.deleted_id}/`)
  }
  return data
}

export const updateNoticeAttachments = async (data: UpdateNoticeAttachmentsPayload): Promise<void> => {
  if (data.attachment && data.phase) {
    const formData = new FormData()
    formData.append('phase', data.phase)
    formData.append('file', data.attachment)
    await api.post(`saw/${data.id}/notices/${data.notice_id}/attachments/`, formData)
  }
  if (data.deleted_id) {
    await api.delete(`saw/${data.id}/notices/${data.notice_id}/attachments/${data.deleted_id}/`)
  }
}

export const getNoticeAttachments = async (data: {
  id: string
  notice_id: string
  phase: string
}): Promise<unknown[]> => {
  const res = await api.get<unknown[]>(`saw/${data.id}/notices/${data.notice_id}/attachments/`, {
    params: { phase: data.phase },
  })
  return res.data
}

export const downloadNoticeZip = async (sawId: string): Promise<{ url: string }> => {
  const res = await api.post<{ url: string }>(`saw/${sawId}/notice_zip/`)
  return res.data
}

// --- SAW Notice Brand Status ---

export const markNoticeBrandCompliant = async (data: {
  saw_id: string
  notice_id: string
  brand_id: string
}): Promise<SawNoticeBrand> => {
  const res = await api.patch<SawNoticeBrand>(
    `saw/${data.saw_id}/notices/${data.notice_id}/brands/${data.brand_id}/mark_as_compliant/`
  )
  return res.data
}

// --- SAW Notice Steps ---

export const sawSendNotice = async (data: SawNoticeStepPayload): Promise<SawNoticeStep> => {
  const res = await api.post<SawNoticeStep>(`saw/${data.id}/notices/${data.notice_id}/steps/`, data)
  return res.data
}

export const bulkSawSendNotice = async (data: BulkSawNoticePayload): Promise<void> => {
  await api.post('saw/bulk-notice/', data)
}

/** Brand-annotated global IPR for bulk send — applied only to notices whose brand matches. */
export interface BulkNoticeIpr {
  ipr_id: string
  brand_id: string
}

/** Per-notice override captured when a target is inspected + saved in the bulk flow. */
export interface BulkSendNoticeTarget {
  notice_id: string
  to_emails?: string[]
  cc?: string[]
  subject?: string
  body?: string
  exclude_ipr_attachments?: string[]
  remarks?: string
}

export interface BulkSendNoticePayload {
  item_ids?: string[]
  saw_ids?: string[]
  notice_ids?: string[]
  template: string
  phase: string
  infringement_type?: string
  /** @deprecated superseded by `iprs`; kept for backward compatibility */
  trademark?: string | null
  iprs?: BulkNoticeIpr[]
  targets?: BulkSendNoticeTarget[]
}

export const bulkSendNotice = async (
  data: BulkSendNoticePayload,
): Promise<{ sent_count: number; skipped?: number }> => {
  const res = await api.post<{ sent_count: number; skipped?: number }>('saw/bulk_send_notice/', data)
  return res.data
}

/** A resolved bulk-notice target: one SawNotice per (saw, brand). */
export interface ResolvedNoticeTarget {
  notice_id: string
  saw_id: string
  domain: string
  brand: { id: string; name: string; logo?: string | null }
  item_count: number
  /**
   * The scoped item ids this target covers (already narrowed to the user's selection for a
   * Site Items flow). Sent back on partial sends so item scoping stays correct when some
   * targets are unchecked.
   */
  item_ids: string[]
  /** IPR ids already asserted on this notice, so the per-domain picker reflects saved state. */
  iprs: string[]
  /** Name of the phase intermediary the notice will go to (null if none on file). */
  intermediary_name?: string | null
  email_config: {
    email: {
      phase_email?: string[] | null
      default_ntd_email?: string | null
      default_ntd_emails_secondary?: string[] | null
      global_email?: string[] | null
    }
    cc: {
      phase_cc?: string[] | null
      default_cc?: string[] | null
      global_cc?: string[] | null
    }
  }
}

export interface ResolveNoticeTargetsPayload {
  item_ids?: string[]
  saw_ids?: string[]
  infringement_type?: string
  phase: string
}

export const resolveNoticeTargets = async (
  data: ResolveNoticeTargetsPayload,
): Promise<ResolvedNoticeTarget[]> => {
  const res = await api.post<ResolvedNoticeTarget[]>('saw/resolve_notice_targets/', data)
  return res.data
}

/* ============================ Bulk Reminder ============================ */

/**
 * A resolved bulk-reminder target: an existing SawNotice (one per (saw, brand)) that already has
 * at least one sent step. Unlike a bulk-send target, the phase is fixed to the notice's LATEST sent
 * step, and the past step's content is returned as seed so Inspect can prefill it.
 */
export interface ResolvedReminderTarget {
  notice_id: string
  saw_id: string
  /** Latest sent step of the notice — the reminder is sent for this step's phase. */
  step_id: string
  domain: string
  brand: { id: string; name: string; logo?: string | null }
  /** Phase of the latest sent step (e.g. "registrant"). */
  phase: string
  item_count: number
  intermediary_name?: string | null
  email_config: ResolvedNoticeTarget['email_config']
  /**
   * Resolved recipient per phase, so the row's phase picker can say up front whether a
   * phase the reminder could be re-pointed at is actually reachable. Keyed by phase id.
   */
  phase_options?: Record<
    string,
    {
      /** Resolved recipients: the phase's own intermediary, else the domain-level default. */
      emails: string[]
      intermediary_name?: string | null
      /** True only when this phase has its own intermediary (not the domain-level fallback). */
      has_intermediary?: boolean
    }
  >
  /** Content of the latest sent step, used to seed the Inspect editor. */
  initial: {
    to_emails?: string[] | null
    cc?: string[] | null
    subject?: string | null
    body?: string | null
    iprs?: string[] | null
  }
}

export interface ResolveReminderTargetsPayload {
  item_ids?: string[]
  saw_ids?: string[]
}

export const resolveReminderTargets = async (
  data: ResolveReminderTargetsPayload,
): Promise<ResolvedReminderTarget[]> => {
  const res = await api.post<ResolvedReminderTarget[]>('saw/resolve_reminder_targets/', data)
  return res.data
}

/** Per-notice override captured when a reminder target is inspected + saved. Phase is required. */
export interface BulkReminderTarget {
  notice_id: string
  phase: string
  to_emails?: string[]
  cc?: string[]
  subject?: string
  body?: string
  exclude_ipr_attachments?: string[]
  remarks?: string
}

export interface BulkSendReminderPayload {
  item_ids?: string[]
  saw_ids?: string[]
  targets?: BulkReminderTarget[]
}

export const bulkSendReminder = async (
  data: BulkSendReminderPayload,
): Promise<{ sent_count: number; skipped?: number }> => {
  const res = await api.post<{ sent_count: number; skipped?: number }>('saw/bulk_send_reminder/', data)
  return res.data
}

export interface BulkCloseNoticeFlowPayload {
  item_ids?: string[]
  saw_ids?: string[]
  notice_ids?: string[]
}

export const bulkCloseNoticeFlow = async (
  data: BulkCloseNoticeFlowPayload,
): Promise<{ closed_count: number }> => {
  const res = await api.post<{ closed_count: number }>('saw/bulk_close_notice_flow/', data)
  return res.data
}

export const uploadSawNoticeStepScreenshot = async (data: {
  saw_id: string
  notice_id: string
  step_id: string
  screenshot: File | null
}): Promise<SawNoticeStep> => {
  const formData = new FormData()
  if (data.screenshot) {
    formData.append('manual_notice_screenshot', data.screenshot)
  }
  formData.append('sent_manually', 'true')
  const res = await api.patch<SawNoticeStep>(
    `saw/${data.saw_id}/notices/${data.notice_id}/steps/${data.step_id}/`,
    formData,
  )
  return res.data
}

export const getSawNoticeBody = async (data: {
  saw_id: string
  infringement_type: string
  step_id: string
  notice_id: string
}): Promise<string> => {
  const res = await api.get<{ body?: string }>(`saw/${data.saw_id}/notices/${data.notice_id}/steps/${data.step_id}/`, {
    params: { fields: 'body' },
  })
  return res.data.body || ''
}

export const getSawNoticePreview = async (data: SawNoticePreviewPayload): Promise<{ subject: string; body: string }> => {
  const res = await api.get<{ subject: string; body: string }>(
    `saw/${data.saw_id}/notices/${data.notice_id}/preview/`,
    { params: { infringement_type: data.infringement_type, template: data.template } }
  )
  return res.data
}

// --- SAW Intermediaries ---

export const getIntermediaries = async (params?: IntermediaryFilters): Promise<{ data: Intermediary[]; count: number }> => {
  const res = await api.get<{ data: Intermediary[]; count: number }>('saw/intermediaries/', { params })
  return res.data
}

export const getIntermediaryDetail = async (data: { id: string }): Promise<Intermediary> => {
  const res = await api.get<Intermediary>(`saw/intermediaries/${data.id}/`)
  return res.data
}

export const intermediaryExists = async (data: { name: string; type: string }): Promise<{ exists: boolean }> => {
  const res = await api.get<{ exists: boolean }>('saw/intermediaries/exists/', { params: data })
  return res.data
}

export const createIntermediary = async (data: CreateIntermediaryPayload): Promise<Intermediary> => {
  const res = await api.post<Intermediary>('saw/intermediaries/', data)
  return res.data
}

export const updateIntermediary = async (data: UpdateIntermediaryPayload): Promise<Intermediary> => {
  const res = await api.patch<Intermediary>(`saw/intermediaries/${data.id}/`, data)
  return res.data
}

export const mergeIntermediary = async (data: { sourceId: string; intoId: string }): Promise<void> => {
  await api.post(`saw/intermediaries/${data.sourceId}/merge/`, { into: data.intoId })
}

export const getIntermediaryGroups = async (params?: { search?: string }): Promise<{ data: IntermediaryGroup[]; count: number }> => {
  const res = await api.get<{ data: IntermediaryGroup[]; count: number }>('saw/intermediary-groups/', { params })
  return res.data
}

export const createIntermediaryGroup = async (data: { name: string }): Promise<IntermediaryGroup> => {
  const res = await api.post<IntermediaryGroup>('saw/intermediary-groups/', data)
  return res.data
}

// --- SAW Comments ---

export const getSawComments = async (params: {
  saw_id: string
  notice?: string
  page?: number | null
  page_size?: number
  expand?: string
}): Promise<{
  data: SawComment[]
  count: number
  totalPages: number
  hasNext: boolean
  nextPage: number | null
}> => {
  const { saw_id, expand = 'user', ...rest } = params
  const res = await api.get<{
    data: SawComment[]
    count: number
    totalPages: number
    hasNext: boolean
    nextPage: number | null
  }>(`saw/${saw_id}/comments/`, {
    params: { ...rest, expand },
  })
  return res.data
}

export const sendSawComment = async (data: CreateSawCommentPayload & { saw_id: string }): Promise<SawComment> => {
  const { saw_id, ...rest } = data
  const res = await api.post<SawComment>(`saw/${saw_id}/comments/`, rest)
  return res.data
}

// --- SAW Whitelisting ---

export const getWhitelistedSAW = async (params: {
  offset?: string
  page?: number
  ordering?: string
  page_size?: number
  search?: string
  fields?: string
  brand?: string
}): Promise<{ data: SAW[]; hasNext: boolean; nextPage: number | null; count: number }> => {
  const res = await api.get<{ data: SAW[]; hasNext: boolean; nextPage: number | null; count: number }>('saw/whitelisted/', { params })
  return res.data
}

export const addSAWToWhitelist = async (data: {
  id?: string
  domain: string
  scope: 'global' | 'member' | 'brand'
  memberId?: string
  brandIds?: string[]
}): Promise<{ id: string; domain: string } | { error: string }> => {
  let sawIdToWhitelist: string

  if (data.id) {
    sawIdToWhitelist = data.id
    if (data.scope === 'global') {
      await api.put(`saw/${sawIdToWhitelist}/`, { whitelisted: true, domain: data.domain })
    }
  } else {
    const createRes = await api.post<{ id: string }>('saw/whitelisted/', { domain: data.domain })
    sawIdToWhitelist = createRes.data.id
  }

  if (data.scope === 'global') {
    return { id: sawIdToWhitelist, domain: data.domain }
  } else if (data.scope === 'member' && data.memberId) {
    const res = await api.post(`members/${data.memberId}/whitelisted_saws/`, { id: sawIdToWhitelist, domain: data.domain })
    return res.data
  } else if (data.scope === 'brand' && data.brandIds && data.brandIds.length > 0) {
    const brandId = data.brandIds[0]
    const res = await api.post(`members/brands/${brandId}/whitelisted_saws/`, { id: sawIdToWhitelist, domain: data.domain })
    return res.data
  } else {
    return { error: 'Invalid scope or missing IDs for member/brand whitelisting.' }
  }
}

export const bulkAddSAWToWhitelist = async (data: {
  domains: string[]
  scope: 'global' | 'member' | 'brand'
  memberId?: string
  brandIds?: string[]
}): Promise<{ data: unknown[] } | { error: string }> => {
  const createRes = await api.post<{ id: string; domain: string }[]>(
    'saw/whitelisted/bulk_create/',
    data.domains.map((domain) => ({ domain }))
  )
  const createdSaws = createRes.data

  if (data.scope === 'global') {
    return { data: createdSaws }
  } else if (data.scope === 'member' && data.memberId) {
    const results = []
    for (const saw of createdSaws) {
      const res = await api.post(`members/${data.memberId}/whitelisted_saws/`, { id: saw.id, domain: saw.domain })
      results.push(res.data)
    }
    return { data: results }
  } else if (data.scope === 'brand' && data.brandIds && data.brandIds.length > 0) {
    const results = []
    for (const brandId of data.brandIds) {
      for (const saw of createdSaws) {
        const res = await api.post(`members/brands/${brandId}/whitelisted_saws/`, { id: saw.id, domain: saw.domain })
        results.push(res.data)
      }
    }
    return { data: results }
  } else {
    return { error: 'Invalid scope or missing IDs for member/brand whitelisting.' }
  }
}

export const removeSAWFromWhitelist = async (id: string): Promise<void> => {
  await api.delete(`saw/whitelisted/${id}/`)
}

export const removeWhitelistedSAWForMember = async (memberId: string, sawId: string): Promise<void> => {
  await api.delete(`members/${memberId}/whitelisted_saws/${sawId}/`)
}

export const removeWhitelistedSAWForBrand = async (brandId: string, sawId: string): Promise<void> => {
  await api.delete(`members/brands/${brandId}/whitelisted_saws/${sawId}/`)
}

export const bulkCheckDuplicateDomains = async (data: { urls: string[] }): Promise<{
  existing_domains: Record<string, { id: string; title: string; status: string }>
  platform_domains: Record<string, { id: string; name: string; logo: string | null }>
  unknown_domains: string[]
}> => {
  const res = await api.post<{
    existing_domains: Record<string, { id: string; title: string; status: string }>
    platform_domains: Record<string, { id: string; name: string; logo: string | null }>
    unknown_domains: string[]
  }>('saw/bulk_check_duplicate_domains/', data)
  return res.data
}

// --- SAW Cases (Under Investigation) ---

export const setSAWUnderInvestigation = async (data: {
  saw_id: string
  label: string
  detail: string
  case_number?: string
  test_purchase?: boolean
  brand_id?: string
}): Promise<unknown> => {
  const payload: Record<string, unknown> = {
    referenced_saw: data.saw_id,
    label: data.label,
    detail: data.detail,
  }
  if (data.case_number && data.case_number.trim() !== '') payload.case_number = data.case_number
  if (data.test_purchase !== undefined) payload.test_purchase = data.test_purchase
  if (data.brand_id !== undefined) payload.brand = data.brand_id

  const res = await api.post('cases/', payload)
  return res.data
}

// --- SAW Brand Remarks ---

export const getSawBrandRemarks = async (sawId: string): Promise<SawBrandRemark[]> => {
  const res = await api.get<SawBrandRemark[]>(`saw/${sawId}/brand_remarks/`)
  return res.data
}

export const getSawBrandRemark = async (sawId: string, brandId: string): Promise<SawBrandRemark> => {
  const res = await api.get<SawBrandRemark>(`saw/${sawId}/brand_remarks/${brandId}/`)
  return res.data
}

export const createSawBrandRemark = async (data: {
  saw_id: string
  brand: string
  remark: string
}): Promise<SawBrandRemark> => {
  const res = await api.post<SawBrandRemark>(`saw/${data.saw_id}/brand_remarks/`, {
    brand: data.brand,
    remark: data.remark,
  })
  return res.data
}

// --- SAW Brand Values ---

export const getSawBrandValues = async (sawId: string): Promise<SawBrandValue[]> => {
  const res = await api.get<SawBrandValue[]>(`saw/${sawId}/brand_values/`)
  return res.data
}

export const getSawBrandValue = async (sawId: string, brandId: string): Promise<SawBrandValue> => {
  const res = await api.get<SawBrandValue>(`saw/${sawId}/brand_values/${brandId}/`)
  return res.data
}

export const updateSawBrandValue = async (
  sawId: string,
  brandId: string,
  data: { total_value?: number | null }
): Promise<SawBrandValue> => {
  const res = await api.patch<SawBrandValue>(`saw/${sawId}/brand_values/${brandId}/`, data)
  return res.data
}

// --- Data Provider Search ---
// All requests are tunneled through the backend proxy:
//   <method> /settings/dataprovider/proxy/<upstream-path>
// The backend forwards query params + JSON body verbatim to api.dataprovider.com/v2
// and manages the upstream OAuth2 session itself (refresh tokens, re-auth).
// The frontend never sees DP credentials.

const DP_PROXY_BASE = 'settings/dataprovider/proxy/'

interface DPRawResponse {
  total?: number
  offset?: number
  limit?: number
  data?: DPSearchRecord | DPSearchRecord[]
}

const normalizeDPResponse = (raw: DPRawResponse): DPSearchResponse => {
  const rawData = raw?.data
  const data: DPSearchRecord[] = Array.isArray(rawData)
    ? rawData
    : rawData
      ? [rawData]
      : []
  return {
    total: typeof raw?.total === 'number' ? raw.total : data.length,
    offset: raw?.offset,
    limit: raw?.limit,
    data,
  }
}

/**
 * Single entry point for every Data Provider search mode. Branches to the
 * matching upstream /search-engine/* endpoint based on `mode` and returns a
 * normalized { total, offset?, limit?, data: [] } shape regardless of whether
 * upstream returned a single record or an array.
 */
// DP's search-engine endpoints cap limit at 25. Clamp here so a stale caller
// can't accidentally trigger a validation error.
const DP_MAX_LIMIT = 25

export const searchDataProvider = async (request: DPSearchRequest): Promise<DPSearchResponse> => {
  const { mode, limit, offset, date, fields } = request
  const body: { fields?: string[] } = {}
  if (fields && fields.length > 0) body.fields = fields

  const commonParams: Record<string, string | number> = {}
  if (typeof offset === 'number') commonParams.offset = offset
  if (typeof limit === 'number') commonParams.limit = Math.min(limit, DP_MAX_LIMIT)
  if (date) commonParams.date = date

  switch (mode) {
    case 'hostname': {
      const { hostname, fuzzy, follow_redirects } = request.params
      const params: Record<string, string | number | boolean> = { ...commonParams }
      if (typeof fuzzy === 'boolean') params.fuzzy = fuzzy
      if (typeof follow_redirects === 'boolean') params.follow_redirects = follow_redirects
      const res = await api.post<DPRawResponse>(
        `${DP_PROXY_BASE}search-engine/hostnames/${encodeURIComponent(hostname)}`,
        body,
        { params },
      )
      return normalizeDPResponse(res.data)
    }

    case 'company': {
      const { companyName, country } = request.params
      const params: Record<string, string | number> = { ...commonParams }
      if (country) params.country = country
      const res = await api.post<DPRawResponse>(
        `${DP_PROXY_BASE}search-engine/companies/${encodeURIComponent(companyName)}`,
        body,
        { params },
      )
      return normalizeDPResponse(res.data)
    }

    case 'keyword': {
      const { keyword, targets } = request.params
      const params: Record<string, string | number | boolean> = { ...commonParams }
      // DP expects each target as its own boolean query param (e.g. ?title=true)
      if (targets && targets.length > 0) {
        for (const target of targets) params[target] = true
      }
      const res = await api.post<DPRawResponse>(
        `${DP_PROXY_BASE}search-engine/keyword/${encodeURIComponent(keyword)}`,
        body,
        { params },
      )
      return normalizeDPResponse(res.data)
    }

    case 'locality': {
      const params: Record<string, string | number> = { ...commonParams }
      const { country, region, city, zip_code, category, website_topic, sic_major_group, gics_sub_industry } = request.params
      if (country) params.country = country
      if (region) params.region = region
      if (city) params.city = city
      if (zip_code) params.zip_code = zip_code
      if (category) params.category = category
      if (website_topic) params.website_topic = website_topic
      if (sic_major_group) params.sic_major_group = sic_major_group
      if (gics_sub_industry) params.gics_sub_industry = gics_sub_industry
      const res = await api.post<DPRawResponse>(
        `${DP_PROXY_BASE}search-engine/locality`,
        body,
        { params },
      )
      return normalizeDPResponse(res.data)
    }

    case 'ip': {
      const res = await api.post<DPRawResponse>(
        `${DP_PROXY_BASE}search-engine/ips/${encodeURIComponent(request.params.ip)}`,
        body,
        { params: commonParams },
      )
      return normalizeDPResponse(res.data)
    }

    case 'phone': {
      const res = await api.post<DPRawResponse>(
        `${DP_PROXY_BASE}search-engine/phonenumbers/${encodeURIComponent(request.params.phoneNumber)}`,
        body,
        { params: commonParams },
      )
      return normalizeDPResponse(res.data)
    }

    case 'domainname': {
      const res = await api.post<DPRawResponse>(
        `${DP_PROXY_BASE}search-engine/domainname/${encodeURIComponent(request.params.domainName)}`,
        body,
        { params: commonParams },
      )
      return normalizeDPResponse(res.data)
    }

    case 'cybersquatting': {
      const res = await api.post<DPRawResponse>(
        `${DP_PROXY_BASE}search-engine/cybersquattingtarget/${encodeURIComponent(request.params.domain)}`,
        body,
        { params: commonParams },
      )
      return normalizeDPResponse(res.data)
    }

    case 'outgoing': {
      const res = await api.post<DPRawResponse>(
        `${DP_PROXY_BASE}search-engine/outgoinghostnames/${encodeURIComponent(request.params.hostname)}`,
        body,
        { params: commonParams },
      )
      return normalizeDPResponse(res.data)
    }
  }
}

// --- Data Provider CONSOLE Search ---
// The internal console API (console.dataprovider.com) behind DP's own web UI,
// tunneled through a SEPARATE backend proxy from the public-API one above:
//   <method> /settings/dataprovider/console/proxy/<upstream-path>
// The backend forwards method + query params + JSON body verbatim and manages
// the upstream Auth0 session. The frontend never sees DP credentials.

const DP_CONSOLE_PROXY_BASE = 'settings/dataprovider/console/proxy/'

// Upstream `terms` returns the top buckets and the UI filters them client-side;
// DP's own UI requests 250.
const DP_CONSOLE_TERMS_SIZE = 250

/**
 * Run a console Search Engine query. `fields[]` drives both the request and the
 * returned columns. Pagination is 0-based `page`/`size` (passed as query params,
 * mirroring the console). Returns the raw `{ count, hits, aggregations }` shape.
 */
export const searchDataProviderConsole = async (
  request: DPConsoleSearchRequest,
): Promise<DPConsoleSearchResponse> => {
  const { page, size, fields, date, filter, field } = request
  const res = await api.post<DPConsoleSearchResponse>(
    `${DP_CONSOLE_PROXY_BASE}api/public/search`,
    {
      product: 'public',
      page,
      size,
      fields,
      date,
      filter: filter ?? null,
      field: field ?? null,
      fixedFields: null,
      excludedFields: null,
    },
    { params: { page, size } },
  )
  return res.data
}

/** Fetch the field catalog: 17 groups / 157 fields. */
export const getConsoleFields = async (): Promise<DPConsoleFieldGroup[]> => {
  const res = await api.get<DPConsoleFieldGroup[]>(
    `${DP_CONSOLE_PROXY_BASE}api/subscriptions/fields`,
    { params: { product: 'public' } },
  )
  return res.data
}

/** Available monthly snapshots (ascending, YYYY-MM). */
export const getConsoleAvailableMonths = async (): Promise<string[]> => {
  const res = await api.get<{ months: string[] }>(
    `${DP_CONSOLE_PROXY_BASE}api/public/available-months`,
  )
  return res.data.months ?? []
}

/**
 * Value autocomplete for a field. Returns the top buckets `{ key, docCount }`;
 * the caller filters/maps them client-side (the endpoint takes no query term).
 */
export const getConsoleTerms = async (
  request: DPConsoleTermsRequest,
): Promise<DPConsoleTermsBucket[]> => {
  const res = await api.post<{ buckets?: DPConsoleTermsBucket[] }>(
    `${DP_CONSOLE_PROXY_BASE}api/public/terms`,
    {
      product: 'public',
      field: request.field,
      date: request.date,
      size: request.size ?? DP_CONSOLE_TERMS_SIZE,
    },
  )
  return res.data.buckets ?? []
}

/**
 * Hostname autocomplete for the relationship-mode seed input (Similar / Same
 * owner / Upsell). Returns matching hostnames for the typed prefix.
 */
export const getConsoleHostnameSuggestions = async (
  input: string,
  date: string,
): Promise<string[]> => {
  const res = await api.post<{ hostnames?: string[] }>(
    `${DP_CONSOLE_PROXY_BASE}api/hostname-auto-complete`,
    { input, date },
  )
  return res.data.hostnames ?? []
}

/** Available change-events for the Event relationship mode (the picker source). */
export const getConsoleImportEvents = async (): Promise<DPConsoleImportEvent[]> => {
  const res = await api.get<DPConsoleImportEvent[]>(`${DP_CONSOLE_PROXY_BASE}api/import-events`)
  return res.data ?? []
}
